// A2F liveness, inferred from what actually arrives per sentence.
//
// Convorcher delivers a sentence as TTS audio plus ARKit blendshape frames from
// the A2F service. Those are separate upstreams: when A2F is down, audio still
// arrives and frames do not, so the avatar TALKS WITH A FROZEN FACE and every
// other signal looks healthy — WS connected, sentences playing, no errors.
//
// That happened on 2026-07-27: the aos-a2f container was `exited:unhealthy`, so
// zero frames were produced for EVERY character. The client had unambiguous
// evidence (audio bytes, frame count 0) and said nothing, so it read as an
// avatar animation bug and burned hours of frontend investigation. The actual
// fix was restarting one service.
//
// Per the repo's UX rules, a missing upstream must fail loudly rather than
// impersonate normal operation — so audio-without-frames is reported, not
// swallowed. This module only OBSERVES; it never gates playback (a frozen face
// with audio still beats silence).
//
// Not React state and not per-frame: called once per delivered sentence.

const FAULT_THRESHOLD = 2

const state = {
  state: 'unknown', // 'unknown' | 'ok' | 'a2f-no-frames'
  sentencesWithAudio: 0,
  sentencesWithFrames: 0,
  consecutiveAudioNoFrames: 0,
  lastSentenceNum: null,
  dismissed: false, // user closed the viewport pill; the fault itself stays true
}

let onWarn = defaultWarn
const listeners = new Set()

function notify() {
  for (const cb of listeners) {
    try {
      cb(getA2fHealth())
    } catch {
      // A broken subscriber must not break sentence playback.
    }
  }
}

function defaultWarn(message) {
  // console.error, not warn: this is an outage, and it must survive the noise of
  // a busy console so whoever is looking sees the cause instead of the symptom.
  if (typeof console !== 'undefined') console.error(message)
}

/**
 * Record what a sentence actually delivered.
 *
 * @param {object}  info
 * @param {number}  [info.sentenceNum]
 * @param {boolean} info.hasAudio    did this sentence deliver any audio?
 * @param {number}  info.frameCount  ARKit frames accumulated for it
 * @returns {string} the resulting health state
 */
export function noteSentenceDelivery({ sentenceNum = null, hasAudio = false, frameCount = 0 } = {}) {
  // A sentence with no audio says nothing about A2F — TTS itself did not
  // deliver, which is a different upstream. Blaming A2F for it would point the
  // next investigation at the wrong service.
  if (!hasAudio) return state.state

  const frames = Number.isFinite(frameCount) ? frameCount : 0
  state.lastSentenceNum = sentenceNum
  state.sentencesWithAudio++

  if (frames > 0) {
    const wasFaulted = state.state === 'a2f-no-frames'
    state.sentencesWithFrames++
    state.consecutiveAudioNoFrames = 0
    state.state = 'ok'
    // Recovery re-arms the pill: a LATER outage is new news, even if the user
    // dismissed the previous one.
    if (wasFaulted) state.dismissed = false
    notify()
    return state.state
  }

  state.consecutiveAudioNoFrames++

  // One zero-frame sentence is normal: convorcher emits audio-bearing terminal
  // chunks whose frames arrived in earlier chunks. Only a streak is evidence.
  if (state.consecutiveAudioNoFrames < FAULT_THRESHOLD) return state.state

  const wasFaulted = state.state === 'a2f-no-frames'
  state.state = 'a2f-no-frames'

  // Announce per transition, not per sentence — a sustained outage must not
  // bury the console (and with it the rest of the diagnosis).
  if (!wasFaulted) {
    onWarn(
      `[a2f] no animation frames in ${state.consecutiveAudioNoFrames} consecutive sentences that DID deliver audio — ` +
        `the face will stay frozen. Audio comes from convorcher/TTS, blendshape frames come from the A2F service, ` +
        `so this points at A2F being down or unreachable rather than at the avatar. ` +
        `Check the a2f service is running before investigating the client. See window.__A2F_HEALTH__.`,
    )
  }
  notify()
  return state.state
}

/**
 * Subscribe to health changes (viewport pill). Returns an unsubscribe fn.
 * Subscription, not polling — the repo's module-singleton convention.
 */
export function subscribeA2fHealth(cb) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

/**
 * Hide the viewport warning. Deliberately does NOT clear the fault — the pill
 * goes away, `state` stays `a2f-no-frames` for anyone reading __A2F_HEALTH__,
 * and a fresh outage after a recovery un-dismisses itself.
 */
export function dismissA2fWarning() {
  state.dismissed = true
  notify()
}

/** Live health snapshot (also exposed as window.__A2F_HEALTH__). */
export function getA2fHealth() {
  return { ...state }
}

/** Reset counters. `onWarn` is a test seam for asserting announce-once. */
export function resetA2fHealth({ onWarn: warn } = {}) {
  state.state = 'unknown'
  state.sentencesWithAudio = 0
  state.sentencesWithFrames = 0
  state.consecutiveAudioNoFrames = 0
  state.lastSentenceNum = null
  state.dismissed = false
  onWarn = warn || defaultWarn
}

// A live getter, so reading it in the console mid-conversation shows the current
// state rather than a snapshot from load time.
if (typeof window !== 'undefined') {
  Object.defineProperty(window, '__A2F_HEALTH__', {
    get: getA2fHealth,
    configurable: true,
  })
}
